/* eslint-disable camelcase */

/** * 列舉付款方式
 * 後續確認其他支付方式，以及現場付款選項
 */
export enum PaymentMethod {
  /** 信用卡 */
  CreditCard = 'CREDIT_CARD',
  /** 金融卡 */
  DebitCard = 'DEBIT_CARD',
  /** LINE Pay */
  LinePay = 'LINE_PAY',
  /** Aftee */
  Aftee = 'AFTEE',
  /** 現金 */
  Cash = 'CASH',
}

/** 卡片支付 (金融卡/信用卡) 的金流供應商 */
export enum CardPaymentVender {
  /** 91App */
  '91App' = 'PAYMENT_91_APP',
  /** TapPay */
  TapPay = 'TAP_PAY',
  /** 藍新 */
  NewebPay = 'NEWEB_PAY',
}

/**
 * 燃油政策類型
 */
export enum FuelPolicyType {
  /** 滿油取還 */
  FullToFull = 'FULL_TO_FULL',
  /** 同取車油量 */
  SameAsPickUp = 'SAME_AS_PICK_UP',
  /** 依行駛里程店家現場收費 */
  ChargeByDrivingMileage = 'CHARGE_BY_DRIVING_MILEAGE',
}

/**
 * 里程政策類型
 */
export enum MileagePolicyType {
  /**
   * 無限里程
   */
  Unlimited = 'UNLIMITED',
  /**
   * 有限里程
   */
  Limited = 'LIMITED',
}

export enum CurrencyCode {
  Twd = 'TWD',
  Usd = 'USD',
  Jpy = 'JPY',
}

interface KeyValueObject {
  key: string;
  value: string;
}

/**
 * 營業日
 */
export enum ShopOpeningDay {
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday',
  Sunday = 'Sunday',
}

type ApiResponseShopOpeningHourPeriod = {
  start: {
    hour: number;
    minute: number;
  };
  end: {
    hour: number;
    minute: number;
  };
};

type ApiResponseShopOpeningHours = {
  [key in ShopOpeningDay]?: {
    periods: ApiResponseShopOpeningHourPeriod[];
  };
}; type CancelRuleItem = {
  /** 幾天前可取消 */
  daysBeforeCancel: number;
  /** 取消日期 */
  cancelDate: string;
  /** 退款百分比 */
  refundPercent: number;
  /** 退款金額 */
  refundMoney: {
    /** 價格 */
    amount: number;
    /** 貨幣 */
    currencyCode: CurrencyCode;
  };
};

type ReservationCancelInfo = {
  selectedRule: CancelRuleItem | null;
  rules: Array<CancelRuleItem>;
  /** 當使用者下訂 ERP 時失敗會啟動自動退款機制，`autoRefundMoney` 紀錄自動退款的金額 */
  autoRefundMoney?: {
    /** 價格 */
    amount: number;
    /** 貨幣 */
    currencyCode: CurrencyCode;
  };
};

/** 發票 scope */
export enum InvoiceScope {
  /** 無 */
  None = 'NONE',
  /** 個人 */
  Personal = 'PERSONAL',
  /** 公司 */
  Company = 'COMPANY',
}

/** Enum: 加購項目類型 */
export enum OptionalItemType {
  /** 可退款 */
  Refundable = 'REFUNDABLE',
  /** 不可退款 */
  NonRefundable = 'NON_REFUNDABLE',
}

export type ApiResponseOptionalItemOutput = {
  id: number;
  /** 加購項目價格 */
  optionalMoney: {
    /** 金額 */
    amount: number;
    /** 貨幣代碼 */
    currencyCode: CurrencyCode;
  };
  /** 數量 */
  quantity: number;
  /** 加購項目類型 */
  type: OptionalItemType;
};

/** 折扣方式 */
export enum DiscountMethod {
  /** None */
  None = 'NONE',
  /** GoPoint */
  GoPoint = 'GO_POINTS',
  /** 優惠代碼 */
  Code = 'PROMOTION',
  /** 優惠券 */
  Coupon = 'COUPON',
}

export type DiscountResponse =
  | {
    type: DiscountMethod.None;
  }
  | {
    /** 優惠方式 */
    type: DiscountMethod.Coupon;
    /** 優惠券張數 (滿千送百優惠券) */
    coupon?: {
      /** 數量 */
      quantity: number;
      /** 優惠金額 */
      discountMoney: {
        /** 金額 */
        amount: number;
        /** 貨幣代碼 */
        currencyCode: CurrencyCode;
      };
    };
  }
  | {
    /** 優惠方式 */
    type: DiscountMethod.Code;
    /** 優惠代碼 */
    promotion: {
      /** 優惠代碼 */
      code: string;
      /** 優惠名稱 */
      name: string;
      /** 優惠金額 */
      discountMoney: {
        /** 金額 */
        amount: number;
        /** 貨幣代碼 */
        currencyCode: CurrencyCode;
      };
    };
  }
  | {
    /** 優惠方式 */
    type: DiscountMethod.GoPoint;
    /** 使用的 GO Point 數量 */
    goPoints: {
      /** 數量 */
      amount: number;
      /** 優惠金額 */
      discountMoney: {
        /** 金額 */
        amount: number;
        /** 貨幣代碼 */
        currencyCode: CurrencyCode;
      };
    };
  };

// ----- Rewards -----
export enum ApiResponseRewardType {
  /** 無 */
  None = 'none',
  /** Go points */
  GoPoints = 'GO_POINTS',
}

export type ApiResponseRewardInfo = {
  /** 是否已獲得獎勵 */
  isRewarded: boolean;
} & (
    | {
      type: ApiResponseRewardType.GoPoints;
      goPoints: {
        /** 獎勵點數 */
        amount: number;
      };
    }
    | { type: ApiResponseRewardType.None }
  );

/**
 * 付款渠道
 */
export enum PaymentChannel {
  /** 線上付款 */
  Online = 'ONLINE',
  /** 現場付款 */
  Offline = 'OFFLINE',
}

export enum Erp {
  /** gogoout (Margin 為 20%) */
  Gogoout_20 = 1,
  /** Hertz */
  Hertz,
  /** Online Travel */
  OnlineTravel,
  /** gogoout (Margin 為 18%) */
  Gogoout_18,
  /** gogoout (Margin 為 16%) */
  Gogoout_16,
}

export type PostReservationMetadata =
  | {
    erp_id: Erp.Gogoout_20 | Erp.Gogoout_18 | Erp.Gogoout_16;
    data: {
      /** 抵達航班號碼 */
      arrival_flight_number?: string;
      /** 抵達航班時間, ISO 8601 without timezone, e.g. "2024-12-25T10:00:00" */
      arrival_flight_time?: string;
      /** 離開航班號碼 */
      departure_flight_number?: string;
      /** 離開航班時間, ISO 8601 without timezone, e.g. "2024-12-27T10:00:00" */
      departure_flight_time?: string;
      /**
       * 店家服務
       * Array of `item.id`
       **/
      service_items?: Array<string>;
    };
  }
  | {
    erp_id: Erp.Hertz;
    data: {
      /** 折扣碼 (CDP) */
      discount_cdp?: string;
      /** 折扣碼 (PC) */
      discount_pc?: string;
      /** 會員 ID */
      membership_id?: string;
    };
  }
  | {
    erp_id: Erp.OnlineTravel;
    data: {
      /** 成人人數 */
      adult_count?: string;
      /** 小孩人數 */
      child_count?: string;
    };
  };

export type ApiResponseShopCancellationPolicy = {
  rules: Array<{
    /** 幾天前可取消 */
    daysBeforeCancellation: number;
    /** 退款百分比 */
    refundPercentage: number;
  }>;
};

enum Country {
  Taiwan = 'TW',
  Japan = 'JP',
  UnitedStates = 'US',
  Korea = 'KR',
  Thailand = 'TH',
}

type GogooutShopResponse = {
  erpId: Erp.Gogoout_20 | Erp.Gogoout_18 | Erp.Gogoout_16;
  metadata: {
    /** 評價 */
    rating?: {
      /** 評價數量 */
      review_count: string;
      /** 總體評分 */
      score: string;
      sub_score: {
        /** 評分維度: 總體服務 */
        overall: string;
        /** 評分維度: 服務流程 */
        service_process: string;
        /** 評分維度: 服務態度 */
        staff_attitude: string;
        /** 評分維度: 車輛整潔 */
        car_cleanliness: string;
      };
    };
    /** 店家服務 */
    store_services?: Array<{
      id: string;
      name: string;
      content: string;
      items: Array<{
        id: string;
        name: string;
      }>;
    }>;
  };
};
type HertzShopResponse = {
  erpId: Erp.Hertz;
  metadata: undefined;
};
type OnlineTravelShopResponse = {
  erpId: Erp.OnlineTravel;
  metadata: undefined;
};

export enum ApiReservationStatus {
  /** 預設狀態 */
  INITIAL = '1',
  /** 付款處理中 */
  PAYMENT_PROCESSING = '2',
  /** 付款逾期 */
  PAYMENT_EXPIRED = '3',
  /** 付款失敗 */
  PAYMENT_FAILURE = '4',
  /** 付款成功 */
  PAYMENT_SUCCESS = '5',
  /** 預訂處理中 */
  RESERVATION_PROCESSING = '6',
  /** 預訂失敗 */
  RESERVATION_FAILURE = '7',
  /** 預訂成功 */
  RESERVATION_SUCCESS = '8',
  /** 取消處理中 */
  CANCELLATION_PROCESSING = '9',
  /** 取消失敗 */
  CANCELLATION_FAILURE = '10',
  /** 取消成功 */
  CANCELLATION_SUCCESS = '11',
  /** 退款處理中 */
  REFUND_PROCESSING = '12',
  /** 退款失敗 */
  REFUND_FAILURE = '13',
  /** 退款成功 */
  REFUND_SUCCESS = '14',
}

export interface ReservationResponse {
  serialNumber: string;
  erpSerialNumber: string;
  localPickUp: string;
  localDropOff: string;
  utcPickUp: string;
  utcDropOff: string;
  userInfo: {
    /** 姓氏 */
    lastName: string;
    /** 名字 */
    firstName: string;
    /** 生日, YYYY-MM-DD */
    birthday: string;
    /** 電子信箱 */
    email: string;
    /** 手機號碼, E.164 format, e.g. "+886912345678" */
    phoneNumber: string;
    /** 旅行證件號碼 */
    travelDocumentNumber: string;
    /** 國家代碼, e.g. "TW" */
    residence: string;
    /** IP 地址 */
    ipAddress: string;
  };
  invoiceInfo: {
    /** 發票類型 */
    type: InvoiceScope;
    /** 公司 */
    company?: {
      /** 公司名稱 */
      name: string;
      /** 公司統一編號 (Unified Business Number) */
      ubn: string;
    };
  };
  carInfo: {
    /** 車輛唯一識別碼 */
    id: string;
    /** 車輛圖片 */
    images: string[];
    /** 車輛名稱 */
    name: string;
    /** 座位數 */
    seat: string;
    /** 手排/自排 */
    transmission: KeyValueObject;
    /** 車輛配備列表 */
    equipments?: KeyValueObject[];
    /** 車輛保險 (含在車價) */
    includedInsurances?: string[];
    /** 車輛價格 */
    saleMoney: {
      /** 價格 */
      amount: number;
      /** 貨幣 */
      currencyCode: CurrencyCode;
    };
    paymentChannel: PaymentChannel;
    metadata?: {
      /** 支援的付款方式 */
      payment_methods: PaymentMethod[];
      /** 超時政策 */
      over_time_policy: {
        /** 超時政策門檻 */
        hour_threshold: string;
        /** 超時政策費用 */
        price_per_hour: {
          /** 費用 */
          amount: string;
          /** 貨幣 */
          currencyCode: CurrencyCode;
        };
      };
      /** 里程政策 */
      mileage_policy: {
        /** 里程政策類型 */
        type: MileagePolicyType;
        /**
         * 里程限制
         * @description 超過 xxx 公里/日
         * @default "0"
         */
        max_km_per_day: string;
        /**
         * 超過里程限制的費用
         * @description 每公里收取 xxx 元
         */
        extra_km_charge?: {
          /** 費用 */
          amount: string;
          /** 貨幣 */
          currencyCode: CurrencyCode;
        };
      };
      /** 燃油政策 */
      fuel_policy: {
        type: FuelPolicyType;
      };
      /** 用車須知 */
      car_usage_guidelines: Array<{
        id: string;
        content: string;
      }>;
      /** 品牌, e.g. Toyota */
      make?: string;
      /** 車齡 */
      age: string;
      /** 車輛的驅動（FWD、RWD、4WD、AWD） */
      drive: string;
      /** 車輛的燃料 */
      fuel: string;
      /** 車輛的空調 */
      air_conditioning: string;
    };
    // ---- 以下為後端未實作的資料 ----
    goPoint?: number;
    /** 推薦程度 */
    rankScore?: number;
  };
  shopInfo: {
    id: number;
    /** 店家 SKU */
    sku?: string;
    /** ERP 編號 */
    erpId: Erp;
    /** 店家地址經緯度 */
    latitude: number;
    longitude: number;
    /** 店家經緯度所在的 H3 地理位置編號 */
    h3cell: string;
    /** 店家名稱 */
    name: string;
    /** 店家地址 */
    address: string;
    /** 店家電話 */
    telephone: string;
    /** 店家圖片, 用於店家頁面 banner */
    bannerImages: string[];
    /** 店家描述 */
    description: string;
    /** 店家自我介紹 */
    introduction: string;
    /** 店家政策 */
    policies?: KeyValueObject[];
    /** 取消政策 */
    cancellationPolicy: ApiResponseShopCancellationPolicy;
    /** 是否接受國籍駕照 */
    allowForeigners: boolean;
    /** 店家所在時區 */
    timezone: string;
    // ---- 以下為後端未實作的資料 ----
    /** 店家所在國家, e.g. TW, JP */
    countryCode?: Country;
    /** 營業時間 */
    openingHours?: ApiResponseShopOpeningHours;
    /** 統一編號 */
    unifiedBusinessNumber?: string;
    /** 稅務相關資料 */
    taxInfo?: {
      taxName: string;
    };
  } & (GogooutShopResponse | HertzShopResponse | OnlineTravelShopResponse);
  paymentInfo: {
    /** 付款管道 */
    channel: PaymentChannel;
    /** 付款方式 */
    method: PaymentMethod;
    /** 卡片廠商 */
    cardVender?: CardPaymentVender;
  } | null;
  status: number;
  createdAt: string;
  updatedAt: string;
  paidAt: string | null;
  refundedAt: string | null;
  expiredAt: string | null;
  reservedAt: string | null;
  cancelledAt: string | null;
  cancelInfo?: ReservationCancelInfo;
  /** 勾選的 ReferralAd id 列表 */
  referralInfo: Array<number>;
  /** 加購項目 */
  optionalInfo?: {
    items: ApiResponseOptionalItemOutput[];
  };
  /** 優惠相關 */
  discountInfo: DiscountResponse;
  /** 獎勵 */
  rewardInfo: ApiResponseRewardInfo;
  /** metadata */
  metadata?: PostReservationMetadata;
};

/**
 * String Enums is not reverse-mapped in TypesScript, so we need to create a reverse mapping function to map in O(1) time.
 *
 * @see https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-4.html#string-enums
 */
export const reverseEnum = <T extends Record<string, string>>(enumObj: T) => {
  // Map<EnumValue, EnumKey>
  const reverseEnumMap = new Map<string, string>()
  for (const [key, value] of Object.entries(enumObj)) {
    reverseEnumMap.set(value, key)
  }

  // EnumValue => EnumObject
  return (value: string): T[keyof T] => {
    const enumKey = reverseEnumMap.get(value)

    if (enumKey === undefined) {
      throw new Error(`Enum value not found: ${value}`)
    }

    return enumObj[enumKey as keyof typeof enumObj]
  }
}

export function reverseReservationStatus (
  reservationStatus: number,
): ApiReservationStatus {
  const parsedReservationStatus = reverseEnum(ApiReservationStatus)
  return parsedReservationStatus(reservationStatus.toString())
}

export type GetReservationsResponse = Array<ReservationResponse>

export type GetReservationDetailResponse = ReservationResponse

export type GetReservationCancelResponse = {
  serialNumber: string;
  cancelInfo: ReservationCancelInfo;
};

export type PostReservationCancelResponse = void;

export type PostReservationResendMailResponse = void;
